import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import NotesTab from '@/billing/component/NotesTab';
import { Sheet, SheetContent } from '@/common/components/ui/sheet';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/common/components/ui/tabs';
import { getEpicPolicyURL } from '@/policy/utils/epicUrl';

import { DrawerHeader } from '../../common/components/drawer/DrawerHeader';
import { BillingSummary } from '../api/FetchBillingList';
import { useOpenedBillingDocument } from '../hooks/useOpenedBillingDocument';
import { BillingDocumentPanel } from './BillingDocumentPanel';
import { DetailsTab } from './DetailsTab';
import { DocumentsTab } from './DocumentsTab';

interface BillingDrawerProps {
  billing: BillingSummary;
  isOpen: boolean;
  onClose: () => void;
  onNavigatePrevious: () => void;
  onNavigateNext: () => void;
  hasPrevious: boolean;
  hasNext: boolean;
}

export const BillingDrawer = ({
  billing,
  isOpen,
  onClose,
  onNavigatePrevious,
  onNavigateNext,
  hasPrevious,
  hasNext,
}: BillingDrawerProps) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('analyse');
  const { openedDocumentId, toggleDocument, closeDocument } =
    useOpenedBillingDocument(billing.uuid, isOpen);
  const [processCodes, setProcessCodes] = useState<Record<string, string>>({});

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    closeDocument();
  };

  const handleClose = () => {
    closeDocument();
    onClose();
  };

  const handleNavigatePrevious = () => {
    closeDocument();
    onNavigatePrevious();
  };

  const handleNavigateNext = () => {
    closeDocument();
    onNavigateNext();
  };

  return (
    <>
      {openedDocumentId !== undefined && (
        <BillingDocumentPanel
          billingUuid={billing.uuid}
          documentId={openedDocumentId}
          onClose={closeDocument}
        />
      )}
      <Sheet open={isOpen} modal={false}>
        <SheetContent
          className="w-full sm:max-w-2xl p-0 top-13.75 h-[calc(100vh-55px)]"
          hideDefaultX
          hideOverlay
        >
          <div className="flex flex-col h-full">
            <DrawerHeader
              title={billing.accountName}
              externalLink={{
                label: `${t('billing.drawer.policy')} ${billing.policyNumber}`,
                path: getEpicPolicyURL(billing.policyGuid) ?? '',
              }}
              onClose={handleClose}
              onNavigatePrevious={handleNavigatePrevious}
              onNavigateNext={handleNavigateNext}
              hasPrevious={hasPrevious}
              hasNext={hasNext}
            />

            <Tabs
              value={activeTab}
              onValueChange={handleTabChange}
              className="flex flex-col flex-1 overflow-hidden gap-0"
            >
              <TabsList className="w-full rounded-none pb-0 bg-primary-foreground h-auto shrink-0">
                <TabsTrigger
                  value="analyse"
                  className="cursor-pointer rounded-none bg-primary-foreground text-foreground dark:text-foreground py-2 border-0 border-b-2 border-transparent data-[state=active]:bg-primary-foreground! data-[state=active]:text-foreground data-[state=active]:border-b-ring! data-[state=active]:shadow-none"
                >
                  {t('billing.drawer.detailsTab')}
                </TabsTrigger>
                <TabsTrigger
                  value="documents"
                  className="cursor-pointer rounded-none bg-primary-foreground text-foreground dark:text-foreground py-2 border-0 border-b-2 border-transparent data-[state=active]:bg-primary-foreground! data-[state=active]:text-foreground data-[state=active]:border-b-ring! data-[state=active]:shadow-none"
                >
                  {t('billing.drawer.documentsTab')}
                </TabsTrigger>
                <TabsTrigger
                  value="notes"
                  className="cursor-pointer rounded-none bg-primary-foreground text-foreground dark:text-foreground py-2 border-0 border-b-2 border-transparent data-[state=active]:bg-primary-foreground! data-[state=active]:text-foreground data-[state=active]:border-b-ring! data-[state=active]:shadow-none"
                >
                  {t('billing.drawer.notes')}
                </TabsTrigger>
              </TabsList>
              <div className="flex-1 overflow-y-auto">
                <TabsContent value="analyse" className="h-full flex flex-col">
                  <DetailsTab billing={billing} onClose={handleClose} />
                </TabsContent>

                <TabsContent value="documents" className="h-full flex flex-col">
                  <DocumentsTab
                    key={billing.uuid}
                    billing={billing}
                    onClose={handleClose}
                    openedDocumentId={openedDocumentId}
                    onToggleDocument={toggleDocument}
                    selectedProcessCode={processCodes[billing.uuid]}
                    onSelectProcess={(code) =>
                      setProcessCodes({ ...processCodes, [billing.uuid]: code })
                    }
                  />
                </TabsContent>

                <TabsContent
                  value="notes"
                  className="h-full min-h-0 flex flex-col"
                >
                  <NotesTab billing={billing} />
                </TabsContent>
              </div>
            </Tabs>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};
